import React from 'react';

import AddIcon from '@mui/icons-material/Add';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import GuideInfoBox from '../components/GuideInfoBox';
import GuideMockButton from '../components/GuideMockButton';
import GuideMockCheckbox from '../components/GuideMockCheckbox';
import GuideMockChip from '../components/GuideMockChip';
import GuideMockContainer from '../components/GuideMockContainer';
import GuidePageLayout from '../components/GuidePageLayout';
import GuideSection from '../components/GuideSection';
import GuideStepList from '../components/GuideStepList';
import GuideTable from '../components/GuideTable';

const GuideContractChecklist: React.FC = () => {
  return (
    <GuidePageLayout
      title="チェックリストの操作・管理"
      description="契約申請のチェックリスト操作と、チェックリスト項目の管理方法です。"
      breadcrumbs={[{ label: '契約管理', path: '/guide/contract/overview' }]}
    >
      {/* チェックリスト操作 */}
      <GuideSection title="チェックリストを操作する（BUC-CT11）">
        <GuideTable
          headers={['条件', '内容']}
          rows={[
            ['必要な権限', '管理部門'],
            ['対象申請', 'ステータスがIN_PROGRESS以降であること'],
          ]}
        />
        <GuideStepList
          steps={[
            { label: '契約申請詳細画面のチェックリストタブを開く' },
            { label: '各チェック項目の内容を確認する' },
            {
              label: '確認済みの項目のチェックボックスをONにする',
              content: (
                <GuideMockContainer label="チェックリスト">
                  <Stack spacing={0.5}>
                    <GuideMockCheckbox label="契約書の確認" checked />
                    <GuideMockCheckbox label="金額の整合性確認" checked />
                    <GuideMockCheckbox label="承認ルートの確認" />
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '承認後、Wチェックが必要な場合は別の担当者がWチェックをONにする',
            },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 1 }}>
          <GuideInfoBox type="warning">
            全チェック項目が完了していないと承認ボタンが押せません。
          </GuideInfoBox>
          <GuideInfoBox>
            チェックを解除するとチェック日時・チェック者もクリアされます。
          </GuideInfoBox>
          <GuideInfoBox>Wチェックは承認後に操作可能になります。</GuideInfoBox>
        </Stack>
      </GuideSection>

      {/* チェックリスト管理 */}
      <GuideSection title="チェックリスト項目を管理する（BUC-CT13）">
        <GuideTable headers={['条件', '内容']} rows={[['必要な権限', '管理者 / 管理部門']]} />
        <GuideStepList
          steps={[
            { label: '契約管理メニューからチェックリスト設定画面を開く' },
            {
              label: '申請種別タブ（新規/変更/解除）を選択する',
              content: (
                <GuideMockContainer label="タブ切り替え">
                  <Stack direction="row" spacing={1}>
                    <GuideMockChip label="新規" color="primary" />
                    <GuideMockChip label="変更" color="default" />
                    <GuideMockChip label="解除" color="default" />
                  </Stack>
                </GuideMockContainer>
              ),
            },
            {
              label: '項目を追加する場合は「新しい項目を追加」をクリック',
              content: (
                <GuideMockContainer label="項目追加">
                  <GuideMockButton
                    label="新しい項目を追加"
                    variant="text"
                    startIcon={<AddIcon />}
                    size="small"
                  />
                </GuideMockContainer>
              ),
            },
            {
              label: '表示順を変更する場合はドラッグ&ドロップで並び替える',
              content: (
                <GuideMockContainer label="並び替え">
                  <Stack spacing={0.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <DragIndicatorIcon
                        sx={{ fontSize: 16, color: 'text.disabled', pointerEvents: 'none' }}
                      />
                      <Typography variant="body2">契約書の確認</Typography>
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <DragIndicatorIcon
                        sx={{ fontSize: 16, color: 'text.disabled', pointerEvents: 'none' }}
                      />
                      <Typography variant="body2">金額の整合性確認</Typography>
                    </Stack>
                  </Stack>
                </GuideMockContainer>
              ),
            },
            { label: '項目を編集する場合はラベルをクリックして修正する' },
            { label: '項目を削除する場合は削除アイコンをクリックする' },
          ]}
        />
        <Stack spacing={1} sx={{ mt: 1 }}>
          <GuideInfoBox>
            削除は論理削除です。削除後の新規申請には紐付けされなくなりますが、既存の申請に紐付いた項目はそのまま残ります。
          </GuideInfoBox>
        </Stack>
      </GuideSection>
    </GuidePageLayout>
  );
};

export default GuideContractChecklist;
